import React, { useEffect, useState } from "react";
import { useAuth } from "../../../app/providers/AuthProvider";
import { admin_apiConfig } from "../../../shared/config/config";
import { getLandingUrl } from "../../../shared/config/landingUrl";
import {
  identityAuthErrorMessage,
  invitationIdentity,
  sendInvitationVerification,
  signInForInvitation,
  signOutWorkspaceIdentity,
  startWorkspaceGoogleSignIn,
} from "../centralIdentityAuth";
import { isManagedSignIn } from "../managedSignIn";

const STORAGE_KEY = "lineside.teamInvitation";
const button =
  "w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold disabled:opacity-50";
const field = "mt-1 w-full rounded-lg border border-slate-300 p-2.5";
const NAME_LIMIT = 100;

// Keeps a claim token across the identity provider's redirect.
export function storeInvitationToken(token) {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* The page continues without a stored continuation. */
  }
}

export function readInvitationToken() {
  const fragment = new URLSearchParams(window.location.hash.slice(1)).get(
    "token",
  );
  if (fragment) {
    storeInvitationToken(fragment);
    return fragment;
  }
  try {
    return sessionStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

// A display name's first word becomes the first name and the rest the last name.
function splitDisplayName(name) {
  const [first = "", ...rest] = String(name || "").trim().split(/\s+/);
  return {
    first_name: first.slice(0, NAME_LIMIT),
    last_name: rest.join(" ").slice(0, NAME_LIMIT),
  };
}

// A claim starts from the account's own name, else from the name on the Google
// account the person signed in with.
function suggestedNames(claim, session, identity) {
  if (claim?.first_name || claim?.last_name)
    return { first_name: claim.first_name || "", last_name: claim.last_name || "" };
  const user = session?.user;
  const sameAccount =
    user?.email &&
    identity?.email &&
    user.email.toLowerCase() === identity.email.toLowerCase();
  return splitDisplayName(
    sameAccount
      ? user.user_metadata?.full_name || user.user_metadata?.name
      : "",
  );
}

// The invitation names its address only as a hint (j***@company.com) until someone signs
// in, which is still enough to tell them straight away that they used the wrong one.
export function matchesEmailHint(email, hint) {
  if (!email || !hint) return true;
  const [hintLocal, hintDomain] = hint.toLowerCase().split("@");
  const [local, domain] = email.toLowerCase().split("@");
  return domain === hintDomain && local.startsWith(hintLocal.replace(/\*+$/, ""));
}

async function invitationRequest(path, token, accessToken, fields = {}) {
  const response = await fetch(
    `${admin_apiConfig.baseURL}/api/organization/team/invitations/${path}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ token, ...fields }),
    },
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(
      typeof result.detail === "string"
        ? result.detail
        : "The invitation could not be processed. Try again.",
    );
  return result;
}

export default function AcceptTeamInvitationPage() {
  const { session } = useAuth();
  const [token] = useState(readInvitationToken);
  const [invitation, setInvitation] = useState(null);
  const [signedIn, setIdentity] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [chosenCreate, setCreateAccount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [terms, setTerms] = useState(false);
  const [names, setNames] = useState(null);
  // An invitation that claims a preconfigured account names the account it claims.
  const claim = invitation?.claim || null;
  // A self-claim starts signed in as the account's managed sign-in, which can't
  // be the person's own; it goes on to a new sign-in, usually a new one.
  const selfClaim = invitation?.origin === "self_claim";
  const identity =
    selfClaim && isManagedSignIn(signedIn?.email) ? null : signedIn;
  // Almost everyone arriving from an invitation is new, so a password starts as a new one.
  const createAccount = chosenCreate ?? true;
  const [usePassword, setUsePassword] = useState(false);
  const wrongAccount =
    !selfClaim && identity && !matchesEmailHint(identity.email, invitation?.email_hint);
  const shownNames = names || suggestedNames(claim, session, identity);
  const setName = (key) => (event) =>
    setNames({ ...shownNames, [key]: event.target.value });

  useEffect(() => {
    let active = true;
    if (!token) {
      setError("Open the invitation link from your email to continue.");
      return undefined;
    }
    invitationRequest("preview", token)
      .then((result) => {
        if (active) setInvitation(result);
      })
      .catch((failure) => {
        if (active) setError(failure.message);
      });
    return () => {
      active = false;
    };
  }, [token]);
  useEffect(() => {
    let active = true;
    invitationIdentity()
      .then((value) => {
        if (active) setIdentity(value);
      })
      .catch((failure) => {
        if (active) setError(identityAuthErrorMessage(failure));
      });
    return () => {
      active = false;
    };
  }, [session?.user?.id]);

  async function run(action) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
    } catch (failure) {
      setError(identityAuthErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  // A claim sends the names the person entered; other invitations send none.
  const accept = () =>
    run(async () => {
      const current = await invitationIdentity();
      const entered = claim
        ? Object.fromEntries(
            Object.entries(shownNames)
              .map(([key, value]) => [key, value.trim()])
              .filter(([, value]) => value),
          )
        : {};
      await invitationRequest("accept", token, current?.token, entered);
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* No stored continuation. */
      }
      window.history.replaceState(null, "", "/accept-invitation");
      setAccepted(true);
    });
  const claimName = [claim?.first_name, claim?.last_name]
    .filter(Boolean)
    .join(" ");
  const claimRole = invitation?.job_title || claim?.role_name || invitation?.role_name;
  const workspace = invitation?.organization_name || "your team";
  const signOut = () =>
    run(async () => {
      await signOutWorkspaceIdentity();
      setIdentity(null);
      setNames(null);
    });

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-5 text-slate-900">
      <section className="w-full max-w-lg space-y-5 rounded-2xl border bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-cyan-800">
          LineSide
        </p>
        <h1 className="text-2xl font-semibold">
          {selfClaim
            ? accepted
              ? "This account is now yours"
              : "Make this account yours"
            : accepted
              ? `You’re in ${workspace}`
              : `Join ${workspace}`}
        </h1>
        {error && (
          <p
            role="alert"
            className="rounded-lg bg-red-50 p-3 text-sm text-red-800"
          >
            {error}
          </p>
        )}
        {message && (
          <p role="status" className="rounded-lg bg-cyan-50 p-3 text-sm">
            {message}
          </p>
        )}
        {accepted ? (
          <div className="space-y-3">
            <a
              className={`${button} block bg-slate-900 text-center text-white`}
              href="/app"
            >
              Open LineSide
            </a>
            <p className="text-sm text-slate-600">
              To let Agents work with your email, connect your Gmail from{" "}
              <a className="underline" href="/app/senders">
                Mailbox access
              </a>
              . You can do this later from your account menu.
            </p>
          </div>
        ) : (
          invitation && (
            <>
              {selfClaim ? (
                <div className="space-y-2 text-sm text-slate-600">
                  <p>
                    You’re making{" "}
                    {claimName ? (
                      <>
                        the account set up for <strong>{claimName}</strong>
                      </>
                    ) : (
                      "the account set up for you"
                    )}
                    {claimRole && (
                      <>
                        {" "}
                        as <strong>{claimRole}</strong>
                      </>
                    )}{" "}
                    yours. It keeps its role, reporting line and work.
                  </p>
                  <p>
                    Sign in with Google, or create your own email and password.
                    It replaces the username and password you were given. With
                    Google, there’s no separate LineSide password. LineSide
                    then reaches you at this email.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 text-sm text-slate-600">
                  <p>
                    {invitation.inviter_name ? (
                      <>
                        <strong>{invitation.inviter_name}</strong> invited you
                      </>
                    ) : (
                      "You’re invited"
                    )}{" "}
                    to join <strong>{workspace}</strong> on LineSide
                    {claimRole && (
                      <>
                        {" "}
                        as <strong>{claimRole}</strong>
                      </>
                    )}
                    .
                  </p>
                  {invitation.email_hint && !identity && (
                    <p>
                      Sign in with <strong>{invitation.email_hint}</strong>, the
                      address this invitation was sent to.
                    </p>
                  )}
                </div>
              )}
              {!identity ? (
                <>
                  <button
                    className={`${button} bg-slate-900 text-white`}
                    disabled={busy}
                    onClick={() => run(startWorkspaceGoogleSignIn)}
                  >
                    Continue with Google
                  </button>
                  {!usePassword && !selfClaim ? (
                    <button
                      className="block w-full text-center text-sm underline"
                      disabled={busy}
                      onClick={() => setUsePassword(true)}
                    >
                      Use an email and password instead
                    </button>
                  ) : (
                  <>
                  <form
                    className="space-y-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      run(async () => {
                        const result = await signInForInvitation(
                          email,
                          password,
                          createAccount,
                        );
                        setIdentity(await invitationIdentity());
                        if (!result.verified)
                          setMessage(
                            "Check your email to confirm your address, then return here.",
                          );
                      });
                    }}
                  >
                    <label className="block text-sm">
                      Email
                      <input
                        required
                        type="email"
                        autoComplete="email"
                        className={field}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </label>
                    <label className="block text-sm">
                      Password
                      <input
                        required
                        minLength={createAccount ? 8 : undefined}
                        type="password"
                        autoComplete={
                          createAccount ? "new-password" : "current-password"
                        }
                        className={field}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </label>
                    {createAccount && (
                      <label className="flex gap-2 text-xs">
                        <input
                          type="checkbox"
                          required
                          checked={terms}
                          onChange={(e) => setTerms(e.target.checked)}
                        />
                        I agree to the{" "}
                        <a
                          className="underline"
                          href={getLandingUrl("/terms")}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Terms of Service
                        </a>
                      </label>
                    )}
                    <button
                      className={`${button} bg-slate-900 text-white`}
                      disabled={busy}
                    >
                      {createAccount ? "Create account" : "Sign in with email"}
                    </button>
                  </form>
                  <button
                    className="text-sm underline"
                    disabled={busy}
                    onClick={() => setCreateAccount(!createAccount)}
                  >
                    {createAccount
                      ? "Already have a LineSide password? Sign in"
                      : "New to LineSide? Create an account"}
                  </button>
                  </>
                  )}
                </>
              ) : wrongAccount ? (
                <>
                  <p
                    role="alert"
                    className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900"
                  >
                    You’re signed in as <strong>{identity.email}</strong>, but this
                    invitation was sent to{" "}
                    <strong>{invitation.email_hint}</strong>. Sign in with that
                    address to join.
                  </p>
                  <button
                    className={`${button} bg-slate-900 text-white`}
                    disabled={busy}
                    onClick={signOut}
                  >
                    Use a different account
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm">
                    Signed in as <strong>{identity.email}</strong>
                  </p>
                  {!identity.verified ? (
                    <>
                      <p className="text-sm text-slate-600">
                        Confirm your email address before joining.
                      </p>
                      <button
                        className={button}
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            setIdentity(await invitationIdentity());
                          })
                        }
                      >
                        I’ve confirmed my email
                      </button>
                      <button
                        className={button}
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            await sendInvitationVerification();
                            setMessage(
                              "Confirmation email sent. Return here after confirming.",
                            );
                          })
                        }
                      >
                        Resend confirmation email
                      </button>
                    </>
                  ) : claim ? (
                    <form
                      className="space-y-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        accept();
                      }}
                    >
                      <p className="text-sm text-slate-600">
                        Last step: how should your team see your name?
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block text-sm">
                          First name
                          <input
                            required
                            maxLength={NAME_LIMIT}
                            autoComplete="given-name"
                            className={field}
                            value={shownNames.first_name}
                            onChange={setName("first_name")}
                          />
                        </label>
                        <label className="block text-sm">
                          Last name
                          <input
                            maxLength={NAME_LIMIT}
                            autoComplete="family-name"
                            className={field}
                            value={shownNames.last_name}
                            onChange={setName("last_name")}
                          />
                        </label>
                      </div>
                      <button
                        className={`${button} bg-slate-900 text-white`}
                        disabled={busy || !shownNames.first_name.trim()}
                      >
                        {busy ? "Joining…" : selfClaim ? "Claim account" : `Join ${workspace}`}
                      </button>
                    </form>
                  ) : (
                    <button
                      className={`${button} bg-slate-900 text-white`}
                      disabled={busy}
                      onClick={accept}
                    >
                      {busy ? "Joining…" : `Join ${workspace}`}
                    </button>
                  )}
                  <button
                    className="text-sm underline"
                    disabled={busy}
                    onClick={signOut}
                  >
                    Use a different account
                  </button>
                </>
              )}
              {selfClaim && (
                <p className="text-xs text-slate-500">
                  Once it’s yours, only your own sign-in opens this account. The
                  username and password you were given stop working.
                </p>
              )}
            </>
          )
        )}
        {!invitation && !error && <p role="status">Loading invitation…</p>}
      </section>
    </main>
  );
}
